// The page constructs a debounced batching JSONP fetcher per signal on
// window.Academia (sitewide_business_elements/asynchronous_item_data_fetcher)
// and leaves several of them idle. They are the only definite source for the
// owner's per-work counts: the rendered badges hide a zero and hide a pending
// request the same way, so reading the DOM cannot tell the two apart.
//
// A fetcher hands $.ajax a success handler and no error handler, and clears its
// queue before the request leaves, so a failed or 404'd batch drops every
// callback silently and looks exactly like a slow one. The success path also
// only walks the ids the response carries, so an id the endpoint declined to
// answer never reaches its callback either. Everything here therefore resolves
// on a deadline with whatever arrived, and a missing entry means unknown,
// never zero.
//
// Getting nothing back is routine rather than exceptional, because both counts
// are owner-only: Api::V0::Works::BookmarkCountsController drops work ids the
// current user did not author and heads :not_found once none survive, and
// Api::V0::MentionsController#work_mentions_count heads :unauthorized unless
// current_user is the viewed user. Ids they do answer are backfilled with an
// explicit 0, so a genuine zero does arrive as 0.
//
// socialDistance is the one signal here that does NOT ride a page fetcher.
// socialDistanceRouteHelper hand-concatenates '?work_ids[]=' onto a URL the
// route helper already ended with '?subdomain_param=api', and that second '?'
// leaves SubdomainParamMiddleware reading "api?work_ids[]=1" as the subdomain,
// so every batch 404s. With no error handler that 404 is spelled exactly like a
// slow request, which is why the arm it feeds rendered control on every
// exposure. It owns its own request instead, over a URL destinations builds and
// pageData's fetch policy.

import { socialDistancePath } from './destinations';
// One deadline for both transports, imported rather than restated so they
// cannot drift. What it covers on the JSONP side is a debounce of 100ms — the
// fetcher shares one queue with the page's own work-strip callers — plus a
// cross-origin round trip, which lands in the same place as pageData's slowest
// fetch. getJson holds the request below to the same figure.
import { DEADLINE_MS, getJson } from './pageData';

// itemId is matched against parseInt() of the response key under CoffeeScript
// ==, which compiles to ===. A stringified work id queues without complaint and
// is then never answered.
type Fetcher = {
  queue: (itemId: number, callback: (value: unknown) => void) => void;
};

export type SocialDistance = {
  // Api::V0::SocialDistancesController counts bookmarks from the last month
  // only, so an older paper with many savers still reads as nothing here.
  //
  // Up to three profiles come back, the first-degree ones distinguishable by
  // carrying no followed_by: ProfileSerializer emits no such key, and the
  // controller attaches one only to second-degree entries. bookmarkerCount sums
  // both degrees uncapped, so it is not the number of people the viewer follows.
  followedByViewer: number;
  atLeast: boolean;
  bookmarkerCount: number;
};

type SocialDistanceEntry = {
  users?: Array<{ followed_by?: unknown }>;
  bookmarker_count?: unknown;
};

// Mirrors Api::V0::SocialDistancesController::MAX_USERS_FOR_SOCIAL_CONTEXT. It
// caps the users array only; bookmarker_count is counted before the sampling.
const MAX_USERS_FOR_SOCIAL_CONTEXT = 3;

// The one window.Academia read in the autotest. It is an alias of window.Aedu
// and the fetchers are attached by the core bundle, so an arm that runs before
// that bundle has executed sees the namespace without them.
function fetcher(name: string): Fetcher | null {
  const academia = (window as unknown as { Academia?: Record<string, unknown> })
    .Academia;
  const candidate = academia?.[name] as Fetcher | undefined;

  return typeof candidate?.queue === 'function' ? candidate : null;
}

function queueAll<T>(
  name: string,
  workIds: number[],
  read: (value: unknown) => T | null,
): Promise<Map<number, T>> {
  const batch = fetcher(name);
  const found = new Map<number, T>();
  if (batch == null || workIds.length === 0) return Promise.resolve(found);

  return new Promise((resolve) => {
    let outstanding = workIds.length;
    let settled = false;

    const settle = () => {
      if (settled) return;
      settled = true;
      resolve(found);
    };

    const deadline = window.setTimeout(settle, DEADLINE_MS);

    workIds.forEach((workId) => {
      batch.queue(workId, (value) => {
        // A batch can still land after the deadline. Writing into the map then
        // would hand two readers of one already-resolved map different answers.
        if (settled) return;

        const parsed = read(value);
        if (parsed != null) found.set(workId, parsed);

        outstanding -= 1;
        if (outstanding > 0) return;

        window.clearTimeout(deadline);
        settle();
      });
    });
  });
}

// Both endpoints render a hash of Ruby Integers, so anything else is a
// malformed payload. Number() cannot be used to widen that: Number(null) is 0,
// which is exactly the "the server said nothing" to "zero" slip this module
// exists to avoid.
function asCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function bookmarkCounts(
  workIds: number[],
): Promise<Map<number, number>> {
  return queueAll('workBookmarkCountsFetcher', workIds, asCount);
}

export function mentionCounts(workIds: number[]): Promise<Map<number, number>> {
  return queueAll('workMentionsCountsFetcher', workIds, asCount);
}

function readDistance(
  entry: SocialDistanceEntry | null,
): SocialDistance | null {
  const users = entry?.users;
  if (!Array.isArray(users)) return null;

  const firstDegree = users.filter((user) => user.followed_by == null).length;

  return {
    followedByViewer: firstDegree,
    // Second-degree profiles only fill the slots the first-degree ones left, so
    // a full house of first-degree ones means the true figure is 3 or more.
    atLeast: firstDegree === MAX_USERS_FOR_SOCIAL_CONTEXT,
    bookmarkerCount: asCount(entry?.bookmarker_count) ?? 0,
  };
}

// The controller renders one entry per work under a `works` hash, keyed by the
// work id as Ruby wrote it — a JSON object key, so a string on arrival. The
// JSONP fetcher used to walk that hash and hand out its values; nothing else
// does now.
function readDistances(payload: unknown): Map<number, SocialDistance> {
  const works = (payload as { works?: unknown } | null)?.works;
  if (works == null || typeof works !== 'object') return new Map();

  const entries = Object.entries(
    works as Record<string, SocialDistanceEntry | null>,
  );

  return new Map(
    entries.flatMap(([key, entry]) => {
      const workId = Number(key);
      if (!Number.isInteger(workId) || workId < 1) return [];

      const distance = readDistance(entry);

      return distance == null ? [] : [[workId, distance] as const];
    }),
  );
}

// Unlike the counts, the controller emits an entry for every id it was asked
// about, so an id missing from this map means the request never landed. An
// empty list is short-circuited rather than asked about: params[:work_ids] of
// [""] would have the controller answer about work 0.
export async function socialDistance(
  workIds: number[],
): Promise<Map<number, SocialDistance>> {
  if (workIds.length === 0) return new Map();

  return readDistances(await getJson(socialDistancePath(workIds)));
}
